import { useState } from 'react'
import { Loader2, Mail, ShieldCheck, ArrowLeft } from 'lucide-react'
import {
  requestPolicyAccessCode,
  verifyPolicyAccessCode,
} from '../../services/policySignService'

const EMAIL_RE = /^[^\s@]+@fastrapid\.com$/i

export default function PolicyGate({ token, policyName, onVerified }) {
  const [step, setStep] = useState('email') // 'email' | 'code'
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')

  const submitEmail = async (e) => {
    e.preventDefault()
    const value = email.trim()
    if (!EMAIL_RE.test(value)) {
      setError('Only @fastrapid.com addresses are allowed.')
      return
    }
    setWorking(true)
    setError('')
    try {
      await requestPolicyAccessCode(token, value)
      setStep('code')
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to send the code. Please try again.')
    } finally {
      setWorking(false)
    }
  }

  const submitCode = async (e) => {
    e.preventDefault()
    if (code.trim().length < 4) {
      setError('Enter the code from your email.')
      return
    }
    setWorking(true)
    setError('')
    try {
      const result = await verifyPolicyAccessCode(token, email.trim(), code.trim())
      if (!result?.access_grant) {
        throw new Error('No access grant returned.')
      }
      onVerified({ email: result.email ?? email.trim(), grant: result.access_grant })
    } catch (err) {
      setError(err.response?.data?.message || 'That code was not accepted. Please try again.')
    } finally {
      setWorking(false)
    }
  }

  const resend = async () => {
    setWorking(true)
    setError('')
    try {
      await requestPolicyAccessCode(token, email.trim())
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to resend the code.')
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-100 sm:p-8">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-rapid-orange">
          <ShieldCheck size={20} aria-hidden />
        </span>
        <div>
          <h1 className="text-base font-bold text-gray-900">Verified access required</h1>
          <p className="text-xs text-gray-500">Sign in with your FastRapid email to continue.</p>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600">
        You have been invited to review and sign{' '}
        <span className="font-semibold text-gray-900">{policyName || 'a policy document'}</span>.
      </p>

      {error ? (
        <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {step === 'email' ? (
        <form onSubmit={submitEmail} className="mt-5 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Work email</span>
            <div className="relative mt-1">
              <Mail size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@fastrapid.com"
                className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-rapid-orange/30"
              />
            </div>
            <span className="mt-1 block text-xs text-gray-400">Only addresses ending in @fastrapid.com are accepted.</span>
          </label>
          <button
            type="submit"
            disabled={working}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-rapid-orange px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#E84A15] disabled:opacity-60"
          >
            {working ? <Loader2 size={16} className="animate-spin" aria-hidden /> : null}
            Send verification code
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="mt-5 space-y-4">
          <p className="text-sm text-gray-600">
            We sent a code to <span className="font-semibold text-gray-900">{email.trim()}</span>. Enter it below.
          </p>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Verification code</span>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              maxLength={8}
              autoComplete="one-time-code"
              placeholder="000000"
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-center text-lg font-bold tracking-[0.4em] outline-none focus:ring-2 focus:ring-rapid-orange/30"
            />
          </label>
          <button
            type="submit"
            disabled={working}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-rapid-orange px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#E84A15] disabled:opacity-60"
          >
            {working ? <Loader2 size={16} className="animate-spin" aria-hidden /> : null}
            Verify and continue
          </button>
          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => { setStep('email'); setCode(''); setError('') }}
              className="inline-flex items-center gap-1 font-semibold text-gray-500 hover:text-gray-800"
            >
              <ArrowLeft size={13} aria-hidden /> Change email
            </button>
            <button
              type="button"
              onClick={resend}
              disabled={working}
              className="font-semibold text-rapid-orange hover:underline disabled:opacity-60"
            >
              Resend code
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
